import EditOutlined from "@mui/icons-material/EditOutlined";
import { IconButton, Stack } from "@mui/material";
import { useCallback, useState, type FC } from "react";
import { useSupabase } from "../../../../server/supabase/hooks";
import type { UUID } from "../../../../utils/uuid";
import { SupabaseDigitalAssetService } from "../../../assets/server/digital-asset-service";
import { EditableField } from "../../../edit-content/editable-field";
import { useLexicalContext } from "../lexical-context";

export const VideoTitle: FC<{
  nodeKey: string;
  asset_id: UUID | undefined;
  title?: string;
  width: string;
  setNodeTitle: (title: string | undefined) => void;
}> = ({ asset_id, title: defaultTitle, width, setNodeTitle }) => {
  const [titleEditable, setTitleEditable] = useState(false);
  const [title, setTitle] = useState(defaultTitle);
  const { channelName: channel } = useLexicalContext();

  const handleSetTitle = useCallback(
    (newTitle: string | undefined) => {
      setTitle(newTitle);
      setNodeTitle(newTitle);
    },
    [setNodeTitle, setTitle],
  );

  useSupabase(
    async ({ supabase }) => {
      if (channel !== "slack") {
        // Only Slack videos should have a title
        handleSetTitle(undefined);
        return;
      }

      if (title || !asset_id) return;
      // if converting from another channel, get the asset title
      const { data: asset } = await new SupabaseDigitalAssetService(supabase).get(asset_id);
      handleSetTitle(asset?.title);
    },
    [asset_id, channel, title, handleSetTitle],
  );

  return (
    channel === "slack" && (
      <Stack gap={1} direction="row" alignItems="center" sx={{ width }}>
        <EditableField
          title={title ?? ""}
          editable={titleEditable}
          setEditable={setTitleEditable}
          setFilename={handleSetTitle}
          onChange={setTitle}
          variant="body2"
          fontWeight={700}
          textColor="#279AFF"
          editableOnClick={false}
          titleStyle={{ marginBottom: 3 }}
        />
        <IconButton
          sx={{ justifyContent: "flex-end", alignSelf: "flex-end" }}
          color="primary"
          data-analytics-id="edit-content-edit-name"
          size="small"
          data-testid="edit-content-edit-name-button"
          onClick={() => {
            setTitleEditable(true);
          }}
        >
          <EditOutlined fontSize="small" />
        </IconButton>
      </Stack>
    )
  );
};
